import { useEffect, useRef, useState } from 'react';

import { useTranslation } from '@/i18n/useTranslation';

import { Button } from './Button';
import { Field } from './Field';

/** What the box *What the recruiter sees* holds — the recruiter's queue row, from what the kiosk holds. */
export interface RecruiterSees {
  /** The entry's name in 0216's form (`Maria A.`), or `undefined` when it has none → «Anonymous». */
  readonly name: string | undefined;
  /** The kiosk attribution, venue · kiosk, or one part alone, or `undefined` when neither is held. */
  readonly site: string | undefined;
}

export interface NeedHelpDialogProps {
  /**
   * `true` when this session has already raised its entry — by this overlay or by another control — so the
   * overlay opens on `in the recruiter queue` and raises nothing [0441 § *When the name is asked*; KOP-1 AC-14].
   */
  readonly alreadyQueued: boolean;
  /** `true` when the session holds a `candidateId`: «Talk to a recruiter» raises straight away (O2). */
  readonly hasRecord: boolean;
  /**
   * `true` only when the session's lookup answered `new` and no profile was created since — the one path on
   * which the kiosk KNOWS there is no record, so the only one that shows «We don't have a record for you
   * yet…» [0441 point 4; the Operator, 2026-10-06, 3.B]. Never inferred from a missing `candidateId`.
   */
  readonly knowsNoRecord: boolean;
  readonly recruiterSees: RecruiterSees;
  /** Raises the entry for a candidate with a record (O2). Called once, by «Talk to a recruiter». */
  readonly onTalkToRecruiter: () => void;
  /** Raises the entry carrying a typed name (O3) — KAD-6j-f's *name entered*. Called once, by the submit. */
  readonly onWaitForAssistance: (name: string) => void;
  /** Dismiss. Nothing is raised and nothing typed is kept. */
  readonly onClose: () => void;
}

const STAGE = { launch: 'launch', name: 'name', queue: 'queue' } as const;
type Stage = (typeof STAGE)[keyof typeof STAGE];

/**
 * *Need help?* — the bubble's overlay, in the three states ISS drew for it [0441; 03-us-kid-7 O1–O4]:
 * `10 · Overlay · Help — summon a rep (launch)`, `— who's asking?` and `— in the recruiter queue`.
 *
 * **Two layouts, read from the frames, not scaled from one** (convention skill § 8): below `sm` the Mobile
 * frames draw a full-screen sheet with a grab handle and a footer note; from `sm` up the iPad and Desktop
 * frames draw a 360-wide card over a light scrim, where the pill sits. Strings only some widths draw render
 * at those widths only [0441 point 8]: the box's footnote below 1440, the footer note below `sm`. The *Reason*
 * row the iPad and Mobile frames draw is not built [0482].
 *
 * ⭐ **The queue state follows the raise and never waits for the write** [0457 point 5; 0046; BR-K7]: it shows
 * the moment the raise is sent, a failed write included, and nothing changes it afterwards. Its body is ISS's
 * string (OQ-152) and renders NOTHING until that string is recorded [0457 point 3] — no placeholder.
 *
 * The header's «AI» mark and «Support · always available» are display only [0441 point 6]: no assistant is
 * built [0169].
 *
 * **Dismissable by × and Escape, not by an outside click** — a palm resting on a touchscreen must not
 * dismiss it, and dismissing is the safe action: it raises nothing.
 */
export function NeedHelpDialog({
  alreadyQueued,
  hasRecord,
  knowsNoRecord,
  recruiterSees,
  onTalkToRecruiter,
  onWaitForAssistance,
  onClose,
}: NeedHelpDialogProps) {
  const t = useTranslation();
  const heading = useRef<HTMLHeadingElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const [stage, setStage] = useState<Stage>(alreadyQueued ? STAGE.queue : STAGE.launch);
  const [name, setName] = useState('');
  const [refused, setRefused] = useState(false);

  /*
   * Focus lands on the heading on mount and AGAIN on every change of state: the control that had focus has
   * just unmounted, and focus falling back to `<body>` loses a keyboard user their place and a screen-reader
   * user the change (convention skill § 9).
   */
  useEffect(() => {
    heading.current?.focus();
  }, [stage]);

  /* Tab stays inside the panel and Escape closes it — the focus loop `ExitConfirmDialog` uses. */
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab' || panel.current === null) return;
      const stops = [...panel.current.querySelectorAll<HTMLElement>('button, input')];
      const first = stops[0];
      const last = stops[stops.length - 1];
      if (first === undefined || last === undefined) return;
      // `at <= 0` covers the heading too: it takes focus on mount and is not itself tabbable.
      const at = stops.indexOf(document.activeElement as HTMLElement);
      const backwards = event.shiftKey && at <= 0;
      /*
       * From the heading, Tab must not walk OUT: in the queue state the × is the only stop and it sits BEFORE
       * the heading, so nothing in the panel follows it and the browser would leave the dialog.
       */
      const nothingAfterHeading =
        at === -1 &&
        !stops.some(
          (stop) =>
            heading.current !== null &&
            (heading.current.compareDocumentPosition(stop) & Node.DOCUMENT_POSITION_FOLLOWING) !==
              0,
        );
      const forwards = !event.shiftKey && (at === stops.length - 1 || nothingAfterHeading);
      if (!backwards && !forwards) return;
      event.preventDefault();
      (backwards ? last : first).focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  const talkToRecruiter = () => {
    // O2: a candidate with a record is never asked a name — the raise goes now. O3: no entry yet.
    if (!hasRecord) {
      setStage(STAGE.name);
      return;
    }
    onTalkToRecruiter();
    setStage(STAGE.queue);
  };

  const headingKey =
    stage === STAGE.launch
      ? 'help.overlay.launch.heading'
      : stage === STAGE.name
        ? 'help.overlay.name.heading'
        : 'help.overlay.queue.heading';

  return (
    /*
      The scrim is the frames' light veil (#f2f4f6 → #d1d3d6, black at ~14%), not the 45% `--scrim` the
      centred dialogs use — the iPad and Desktop frames keep the page readable behind the card. At 375 the
      sheet covers the whole viewport, so the scrim is never seen there.
    */
    <div className="fixed inset-0 z-50 bg-[rgb(0_0_0/0.14)]">
      <div
        aria-labelledby="need-help-title"
        aria-modal="true"
        className="fixed inset-0 flex flex-col bg-[var(--card)] sm:inset-auto sm:bottom-[88px] sm:right-6 sm:w-[360px] sm:rounded-[var(--radius-lg)] sm:shadow-[shadow:var(--elevation-3)] min-[1440px]:bottom-[179px] min-[1440px]:right-5"
        ref={panel}
        role="dialog"
      >
        {/* The Mobile frames' grab handle — decorative, nothing drags. */}
        <div
          aria-hidden="true"
          className="mx-auto mt-3 h-1 w-9 shrink-0 rounded-[var(--radius-full)] bg-[var(--border-subtle)] sm:hidden"
        />
        {/* 60 tall from `sm`; at 375 the avatar sits 17 below the handle and 15 above the divider. */}
        <div className="mt-[17px] flex shrink-0 items-center gap-2.5 border-b border-[var(--divider-border)] px-4 pb-[15px] sm:mt-0 sm:h-[60px] sm:pb-0">
          <span
            aria-hidden="true"
            className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-full)] bg-[var(--primary)] text-xs font-semibold text-[var(--primary-foreground)]"
          >
            {t('help.overlay.avatar')}
          </span>
          <span className="flex-1 text-sm font-semibold leading-8 text-[var(--foreground)]">
            {t('help.overlay.support')}
          </span>
          <button
            aria-label={t('help.overlay.close')}
            // The hit area stays 44 [§ 9]; below `sm` `-my-1.5` keeps it from growing the 32-tall drawn row.
            className="kiosk-focusable -mr-1 -my-1.5 flex size-11 shrink-0 sm:my-0 items-center justify-center rounded-[var(--radius-sm)] text-[var(--foreground)]"
            onClick={onClose}
            type="button"
          >
            <svg aria-hidden="true" className="size-4" fill="none" viewBox="0 0 16 16">
              <path
                d="M3.5 3.5l9 9m0-9l-9 9"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.5"
              />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-3 overflow-y-auto p-4 sm:flex-none">
          <h2
            className={`kiosk-heading-focus text-base font-semibold leading-6 ${
              // The frames draw the queue title near-black and the other two in the header's grey.
              stage === STAGE.queue ? 'text-[var(--strong-foreground)]' : 'text-[var(--foreground)]'
            }`}
            id="need-help-title"
            ref={heading}
            tabIndex={-1}
          >
            {t(headingKey)}
          </h2>

          {stage === STAGE.launch && (
            <>
              <p className="-mt-2 text-sm leading-5 text-[var(--muted-foreground)]">
                {t('help.overlay.launch.body')}
              </p>
              <Button fullWidth onClick={talkToRecruiter}>
                {t('help.overlay.launch.submit')}
              </Button>
            </>
          )}

          {stage === STAGE.name && (
            <form
              className="flex flex-col gap-3"
              noValidate
              onSubmit={(event) => {
                // Owned here: a native submission would navigate the kiosk away, and Enter triggers one.
                event.preventDefault();
                const trimmed = name.trim();
                // The frames draw the button enabled over an empty field, and draw the error [0441].
                if (trimmed === '') {
                  setRefused(true);
                  return;
                }
                onWaitForAssistance(trimmed);
                setStage(STAGE.queue);
              }}
            >
              {knowsNoRecord && (
                <p className="-mt-2 text-sm leading-5 text-[var(--muted-foreground)]">
                  {t('help.overlay.name.noRecord')}
                </p>
              )}
              <Field
                error={refused ? t('help.overlay.name.error') : undefined}
                hint={t('help.overlay.name.hint')}
                id="need-help-name"
                label={t('help.overlay.name.label')}
                onChange={(value) => {
                  setName(value);
                  setRefused(false);
                }}
                placeholder={t('help.overlay.name.placeholder')}
                required
                value={name}
              />
              <Button fullWidth type="submit">
                {t('help.overlay.name.submit')}
              </Button>
            </form>
          )}

          {stage === STAGE.queue && (
            // `role="status"` so the change is announced, not only rendered. The body is ISS's string
            // (OQ-152) and renders nothing until it is recorded [0457 point 3].
            <div className="flex flex-col gap-3" role="status">
              <div className="flex flex-col gap-1.5 rounded-[var(--radius-sm)] bg-[var(--scan-surface)] p-3 text-xs leading-4">
                <p className="text-[10px] font-semibold leading-4 text-[var(--muted-foreground)]">
                  {t('help.overlay.queue.boxHeading')}
                </p>
                <BoxRow
                  label={t('help.overlay.queue.name')}
                  value={recruiterSees.name ?? t('help.overlay.queue.anonymous')}
                />
                <BoxRow label={t('help.overlay.queue.site')} value={recruiterSees.site} />
                {/*
                  No *Reason* row, at any width, though the iPad and Mobile frames draw one: the reason is the
                  recruiter's alone [0482, amending 0441 point 5; 0045].
                */}
              </div>
              <p className="text-[11px] leading-4 text-[var(--muted-foreground)] min-[1440px]:hidden">
                {t('help.overlay.queue.footnote')}
              </p>
            </div>
          )}
        </div>

        <p className="mt-auto shrink-0 border-t border-[var(--divider-border)] bg-[var(--muted)] px-4 py-3 text-center text-xs leading-4 text-[var(--muted-foreground)] sm:hidden">
          {t('help.overlay.stillOpen')}
        </p>
      </div>
    </div>
  );
}

interface BoxRowProps {
  readonly label: string;
  readonly value: string | undefined;
}

/** One row of the box: the label left, the value right. An absent value renders nothing in its place. */
function BoxRow({ label, value }: BoxRowProps) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <span className="text-[var(--muted-foreground)]">{label}</span>
      <span className="text-right font-semibold text-[var(--strong-foreground)]">{value}</span>
    </div>
  );
}
